import { EMPTY_VALUE } from "@rumbledirect/ui-v2/format";
import type {
  ChargingAssignmentType,
  MobilityCardStatus,
  MobilityCardType,
  ChargingContractStatus,
  ChargingContractType,
} from "./charging";

/**
 * Dutch labels for the charging enums, mirroring the backend's
 * `messages.nl.yaml`. Each label map is ordered the way the backend enum is, so
 * a select built from it reads in the same order as the API documents it.
 */
export const contractTypeLabels: Record<ChargingContractType, string> = {
  public_roaming: "Publiek laden",
  home: "Thuisladen",
  workplace: "Laden op werk",
};

export const contractStatusLabels: Record<ChargingContractStatus, string> = {
  active: "Actief",
  suspended: "Opgeschort",
  terminated: "Beëindigd",
};

export const cardTypeLabels: Record<MobilityCardType, string> = {
  charging: "Laadpas",
  fuel: "Tankpas",
  hybrid: "Combinatiepas",
};

export const cardStatusLabels: Record<MobilityCardStatus, string> = {
  ordered: "Besteld",
  active: "Actief",
  blocked: "Geblokkeerd",
  lost: "Verloren",
  expired: "Verlopen",
  terminated: "Beëindigd",
};

export const assignmentTypeLabels: Record<ChargingAssignmentType, string> = {
  employee: "Medewerker",
  vehicle: "Voertuig",
};

/**
 * Resolves a raw enum value to its Dutch label. Falls back to the raw value so a
 * value added to the backend before this list still reads as something, and to a
 * dash when there is nothing to show at all.
 */
function labelFrom(
  labels: Record<string, string>,
  value: string | null | undefined,
): string {
  if (!value) return EMPTY_VALUE;
  return labels[value] ?? value;
}

export function contractTypeLabel(value: string | null | undefined): string {
  return labelFrom(contractTypeLabels, value);
}

export function contractStatusLabel(value: string | null | undefined): string {
  return labelFrom(contractStatusLabels, value);
}

export function cardTypeLabel(value: string | null | undefined): string {
  return labelFrom(cardTypeLabels, value);
}

export function cardStatusLabel(value: string | null | undefined): string {
  return labelFrom(cardStatusLabels, value);
}

export function assignmentTypeLabel(value: string | null | undefined): string {
  return labelFrom(assignmentTypeLabels, value);
}

/** The contract statuses as ordered options for a filter or select. */
export const contractStatusOptions: readonly {
  value: ChargingContractStatus;
  label: string;
}[] = (Object.keys(contractStatusLabels) as ChargingContractStatus[]).map(
  (value) => ({ value, label: contractStatusLabels[value] }),
);

/** The contract types as ordered options for a filter or select. */
export const contractTypeOptions: readonly {
  value: ChargingContractType;
  label: string;
}[] = (Object.keys(contractTypeLabels) as ChargingContractType[]).map(
  (value) => ({ value, label: contractTypeLabels[value] }),
);

/** The card types as ordered options for a select. */
export const cardTypeOptions: readonly {
  value: MobilityCardType;
  label: string;
}[] = (Object.keys(cardTypeLabels) as MobilityCardType[]).map((value) => ({
  value,
  label: cardTypeLabels[value],
}));

/**
 * The card statuses a card can be given when it is first registered. A card is
 * either on its way ("besteld") or already in the employee's hand ("actief"); the
 * remaining statuses describe things that happen to a card later, so offering
 * them here would only invite nonsense.
 */
export const initialCardStatusOptions: readonly {
  value: MobilityCardStatus;
  label: string;
}[] = (["ordered", "active"] as MobilityCardStatus[]).map((value) => ({
  value,
  label: cardStatusLabels[value],
}));

/** Tariffs are stored in ten-thousandths of a euro, so €0.2900/kWh is `2900`. */
const TARIFF_UNITS_PER_EURO = 10_000;

const tariffFormatter = new Intl.NumberFormat("nl-NL", {
  style: "currency",
  currency: "EUR",
  minimumFractionDigits: 4,
  maximumFractionDigits: 4,
});

/**
 * Formats a kWh tariff for display. The four decimals are not cosmetic: charging
 * tariffs are quoted per kWh at that precision, so rounding to cents would show
 * a different price than the contract states.
 */
export function formatTariffPerKwh(value: number | null | undefined): string {
  if (value === null || value === undefined) return EMPTY_VALUE;
  return `${tariffFormatter.format(value / TARIFF_UNITS_PER_EURO)} per kWh`;
}
